import { useState, useCallback } from 'react'
import { apiClient } from '@/lib/api'
import type { ChartDataPoint } from '@/components/charts/ReturnsChart'
import type { TickerResult } from '@/components/tables/ReturnsTable'

// Request types for the calculate API
export interface CalculationRequest {
  tickers: string[]
  startDate: string
  endDate: string
  reinvestDividends: boolean
  adjustInflation: boolean
  initialInvestment: number
  requestedMetrics?: string[]  // Optional: only calculate these metrics
}

// API response types - matches the actual backend response
interface TickerDataResponse {
  symbol: string
  dates: string[]
  values: string[]  // Backend returns strings for Decimal precision
  values_without_drip: string[] | null  // Non-DRIP values for comparison
  total_return_percent: string
  annualized_return_percent: string
  dividends_received: string
  shares_from_drip: string
  // Enhanced metrics
  inception_date: string | null
  final_value_0_percent: string | null
  final_value_50_percent: string | null
  final_value_100_percent: string | null
  flat_threshold_percent: string | null
  dividend_yield: string | null
  max_drawdown_percent: string | null
  volatility_percent: string | null
  dividend_growth_rate_percent: string | null
  underlying_ticker: string | null
  // Share price fields
  start_price: string | null
  end_price: string | null
  price_change_dollars: string | null
  price_change_percent: string | null
}

interface CalculationApiResponse {
  tickers: TickerDataResponse[]
  metadata: {
    start_date: string
    end_date: string
    reinvest_dividends: boolean
    adjust_inflation: boolean
    initial_investment: string
    inflation_rate: number | null
    calculation_timestamp: string
  }
}

// Transformed data for components
export interface CalculationData {
  chartData: ChartDataPoint[]
  tableData: TickerResult[]
  metadata: {
    calculatedAt: string
    initialInvestment: number
    reinvestDividends: boolean
    adjustedForInflation: boolean
  }
}

// Request type for single ticker calculation
export interface SingleTickerRequest {
  ticker: string
  startDate: string
  endDate: string
  reinvestDividends: boolean
  adjustInflation: boolean
  initialInvestment: number
  requestedMetrics?: string[]  // Optional: only calculate these metrics
}

// Request type for batch calculation
export interface BatchCalculationRequest {
  tickers: string[]
  startDate: string
  endDate: string
  reinvestDividends: boolean
  adjustInflation: boolean
  initialInvestment: number
  requestedMetrics?: string[]  // Optional: only calculate these metrics
}

// Track which tickers are currently loading (for optimistic UI)
export interface LoadingTicker {
  symbol: string
  startedAt: number
}

interface UseCalculationReturn {
  loading: boolean
  loadingTickers: LoadingTicker[]
  error: string | null
  data: CalculationData | null
  calculate: (request: CalculationRequest) => Promise<void>
  calculateSingle: (request: SingleTickerRequest) => Promise<void>
  calculateBatch: (request: BatchCalculationRequest) => Promise<void>
  removeTickerData: (ticker: string) => void
  reset: () => void
}

// Single ticker API response type
interface SingleTickerApiResponse {
  ticker: TickerDataResponse
  metadata: {
    start_date: string
    end_date: string
    reinvest_dividends: boolean
    adjust_inflation: boolean
    initial_investment: string
    inflation_rate: number | null
    calculation_timestamp: string
  }
}

/**
 * Transform a single ticker response to component format
 */
function transformSingleTicker(ticker: TickerDataResponse): { chartData: ChartDataPoint; tableData: TickerResult } {
  const values = ticker.values.map((v) => parseFloat(v))
  const finalValue = values.length > 0 ? values[values.length - 1] : 0

  return {
    chartData: {
      ticker: ticker.symbol,
      dates: ticker.dates,
      values: values,
      valuesWithoutDrip: ticker.values_without_drip
        ? ticker.values_without_drip.map((v) => parseFloat(v))
        : null,
    },
    tableData: {
      ticker: ticker.symbol,
      totalReturn: parseFloat(ticker.total_return_percent),
      cagr: parseFloat(ticker.annualized_return_percent),
      finalValue: finalValue,
      dividendsReceived: parseFloat(ticker.dividends_received),
      inceptionDate: ticker.inception_date,
      finalValue0Percent: ticker.final_value_0_percent ? parseFloat(ticker.final_value_0_percent) : null,
      finalValue50Percent: ticker.final_value_50_percent ? parseFloat(ticker.final_value_50_percent) : null,
      finalValue100Percent: ticker.final_value_100_percent ? parseFloat(ticker.final_value_100_percent) : null,
      flatThresholdPercent: ticker.flat_threshold_percent ? parseFloat(ticker.flat_threshold_percent) : null,
      dividendYield: ticker.dividend_yield ? parseFloat(ticker.dividend_yield) : null,
      maxDrawdownPercent: ticker.max_drawdown_percent ? parseFloat(ticker.max_drawdown_percent) : null,
      volatilityPercent: ticker.volatility_percent ? parseFloat(ticker.volatility_percent) : null,
      dividendGrowthRatePercent: ticker.dividend_growth_rate_percent ? parseFloat(ticker.dividend_growth_rate_percent) : null,
      underlyingTicker: ticker.underlying_ticker,
      startPrice: ticker.start_price ? parseFloat(ticker.start_price) : null,
      endPrice: ticker.end_price ? parseFloat(ticker.end_price) : null,
      priceChangeDollars: ticker.price_change_dollars ? parseFloat(ticker.price_change_dollars) : null,
      priceChangePercent: ticker.price_change_percent ? parseFloat(ticker.price_change_percent) : null,
    },
  }
}

/**
 * Transform API response to format needed by chart and table components
 */
function transformResponse(response: CalculationApiResponse): CalculationData {
  // Transform ticker data for the chart
  const chartData: ChartDataPoint[] = response.tickers.map((ticker) => ({
    ticker: ticker.symbol,
    dates: ticker.dates,
    values: ticker.values.map((v) => parseFloat(v)),  // Convert strings to numbers
    valuesWithoutDrip: ticker.values_without_drip
      ? ticker.values_without_drip.map((v) => parseFloat(v))
      : null,
  }))

  // Transform ticker data for the table
  const tableData: TickerResult[] = response.tickers.map((ticker) => {
    const values = ticker.values.map((v) => parseFloat(v))
    const finalValue = values.length > 0 ? values[values.length - 1] : 0

    return {
      ticker: ticker.symbol,
      totalReturn: parseFloat(ticker.total_return_percent),
      cagr: parseFloat(ticker.annualized_return_percent),
      finalValue: finalValue,
      dividendsReceived: parseFloat(ticker.dividends_received),
      // Enhanced metrics - parse if present
      inceptionDate: ticker.inception_date,
      finalValue0Percent: ticker.final_value_0_percent ? parseFloat(ticker.final_value_0_percent) : null,
      finalValue50Percent: ticker.final_value_50_percent ? parseFloat(ticker.final_value_50_percent) : null,
      finalValue100Percent: ticker.final_value_100_percent ? parseFloat(ticker.final_value_100_percent) : null,
      flatThresholdPercent: ticker.flat_threshold_percent ? parseFloat(ticker.flat_threshold_percent) : null,
      dividendYield: ticker.dividend_yield ? parseFloat(ticker.dividend_yield) : null,
      maxDrawdownPercent: ticker.max_drawdown_percent ? parseFloat(ticker.max_drawdown_percent) : null,
      volatilityPercent: ticker.volatility_percent ? parseFloat(ticker.volatility_percent) : null,
      dividendGrowthRatePercent: ticker.dividend_growth_rate_percent ? parseFloat(ticker.dividend_growth_rate_percent) : null,
      underlyingTicker: ticker.underlying_ticker,
      // Share price fields
      startPrice: ticker.start_price ? parseFloat(ticker.start_price) : null,
      endPrice: ticker.end_price ? parseFloat(ticker.end_price) : null,
      priceChangeDollars: ticker.price_change_dollars ? parseFloat(ticker.price_change_dollars) : null,
      priceChangePercent: ticker.price_change_percent ? parseFloat(ticker.price_change_percent) : null,
    }
  })

  return {
    chartData,
    tableData,
    metadata: {
      calculatedAt: response.metadata.calculation_timestamp,
      initialInvestment: parseFloat(response.metadata.initial_investment),
      reinvestDividends: response.metadata.reinvest_dividends,
      adjustedForInflation: response.metadata.adjust_inflation,
    },
  }
}

/**
 * Custom hook for managing calculation API calls
 * Handles loading state, errors, and data transformation
 * Supports incremental single-ticker calculations for better UX
 */
export function useCalculation(): UseCalculationReturn {
  const [loading, setLoading] = useState(false)
  const [loadingTickers, setLoadingTickers] = useState<LoadingTicker[]>([])
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<CalculationData | null>(null)

  // Remove a ticker from the cached data
  const removeTickerData = useCallback((ticker: string) => {
    setData((prev) => {
      if (!prev) return null
      return {
        ...prev,
        chartData: prev.chartData.filter((d) => d.ticker !== ticker),
        tableData: prev.tableData.filter((d) => d.ticker !== ticker),
      }
    })
  }, [])

  // Calculate a single ticker and merge into existing data
  const calculateSingle = useCallback(async (request: SingleTickerRequest) => {
    const ticker = request.ticker.toUpperCase().trim()

    // Add to loading tickers for optimistic UI
    setLoadingTickers((prev) => [...prev, { symbol: ticker, startedAt: Date.now() }])
    setError(null)

    try {
      const response = await apiClient.post<SingleTickerApiResponse>('/calculate-single', {
        ticker: ticker,
        start_date: request.startDate,
        end_date: request.endDate,
        reinvest_dividends: request.reinvestDividends,
        adjust_inflation: request.adjustInflation,
        initial_investment: request.initialInvestment,
        requested_metrics: request.requestedMetrics,
      })

      const { chartData: newChartData, tableData: newTableData } = transformSingleTicker(response.ticker)

      // Merge into existing data or create new data
      setData((prev) => {
        if (!prev) {
          // No existing data, create new
          return {
            chartData: [newChartData],
            tableData: [newTableData],
            metadata: {
              calculatedAt: response.metadata.calculation_timestamp,
              initialInvestment: parseFloat(response.metadata.initial_investment),
              reinvestDividends: response.metadata.reinvest_dividends,
              adjustedForInflation: response.metadata.adjust_inflation,
            },
          }
        }

        // Check if ticker already exists (shouldn't happen, but handle it)
        const existingChartIndex = prev.chartData.findIndex((d) => d.ticker === ticker)
        const existingTableIndex = prev.tableData.findIndex((d) => d.ticker === ticker)

        let newChartDataArray = [...prev.chartData]
        let newTableDataArray = [...prev.tableData]

        if (existingChartIndex >= 0) {
          newChartDataArray[existingChartIndex] = newChartData
        } else {
          newChartDataArray.push(newChartData)
        }

        if (existingTableIndex >= 0) {
          newTableDataArray[existingTableIndex] = newTableData
        } else {
          newTableDataArray.push(newTableData)
        }

        return {
          ...prev,
          chartData: newChartDataArray,
          tableData: newTableDataArray,
          metadata: {
            ...prev.metadata,
            calculatedAt: response.metadata.calculation_timestamp,
          },
        }
      })

      // Note: Background calculation removed - backend now uses pre-calculated data
      // which is fast enough to return all metrics in the initial request
    } catch (err: unknown) {
      // Handle different error types
      if (err instanceof Error) {
        const axiosError = err as { response?: { data?: { detail?: string; message?: string } } }
        if (axiosError.response?.data) {
          const errorData = axiosError.response.data
          setError(errorData.detail || errorData.message || `Failed to calculate ${ticker}`)
        } else {
          setError(err.message || `Failed to calculate ${ticker}`)
        }
      } else {
        setError(`Failed to calculate ${ticker}`)
      }
    } finally {
      // Remove from loading tickers
      setLoadingTickers((prev) => prev.filter((t) => t.symbol !== ticker))
    }
  }, [])

  // Calculate multiple tickers in a single batch request
  const calculateBatch = useCallback(async (request: BatchCalculationRequest) => {
    if (!request.tickers || request.tickers.length === 0) {
      return
    }

    const normalizedTickers = request.tickers.map(t => t.toUpperCase().trim())

    // Add all tickers to loading state
    const now = Date.now()
    setLoadingTickers(normalizedTickers.map(symbol => ({ symbol, startedAt: now })))
    setError(null)

    try {
      const response = await apiClient.post<CalculationApiResponse>('/calculate-batch', {
        tickers: normalizedTickers,
        start_date: request.startDate,
        end_date: request.endDate,
        reinvest_dividends: request.reinvestDividends,
        adjust_inflation: request.adjustInflation,
        initial_investment: request.initialInvestment,
        requested_metrics: request.requestedMetrics,
      })

      const transformedData = transformResponse(response)

      // Merge into existing data or replace entirely
      setData((prev) => {
        if (!prev) {
          return transformedData
        }

        // Merge: update existing tickers and add new ones
        const newChartDataArray = [...prev.chartData]
        const newTableDataArray = [...prev.tableData]

        for (const chartItem of transformedData.chartData) {
          const existingIndex = newChartDataArray.findIndex(d => d.ticker === chartItem.ticker)
          if (existingIndex >= 0) {
            newChartDataArray[existingIndex] = chartItem
          } else {
            newChartDataArray.push(chartItem)
          }
        }

        for (const tableItem of transformedData.tableData) {
          const existingIndex = newTableDataArray.findIndex(d => d.ticker === tableItem.ticker)
          if (existingIndex >= 0) {
            newTableDataArray[existingIndex] = tableItem
          } else {
            newTableDataArray.push(tableItem)
          }
        }

        return {
          ...prev,
          chartData: newChartDataArray,
          tableData: newTableDataArray,
          metadata: transformedData.metadata,
        }
      })
    } catch (err: unknown) {
      if (err instanceof Error) {
        const axiosError = err as { response?: { data?: { detail?: string; message?: string } } }
        if (axiosError.response?.data) {
          const errorData = axiosError.response.data
          setError(errorData.detail || errorData.message || 'Batch calculation failed')
        } else {
          setError(err.message || 'An unexpected error occurred')
        }
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      // Clear all loading tickers
      setLoadingTickers([])
    }
  }, [])

  const calculate = useCallback(async (request: CalculationRequest) => {
    // Validate request
    if (!request.tickers || request.tickers.length === 0) {
      setError('Please add at least one ticker symbol')
      return
    }

    if (!request.startDate || !request.endDate) {
      setError('Please select a valid date range')
      return
    }

    if (new Date(request.startDate) >= new Date(request.endDate)) {
      setError('Start date must be before end date')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await apiClient.post<CalculationApiResponse>('/calculate', {
        tickers: request.tickers,
        start_date: request.startDate,
        end_date: request.endDate,
        reinvest_dividends: request.reinvestDividends,
        adjust_inflation: request.adjustInflation,
        initial_investment: request.initialInvestment,
        requested_metrics: request.requestedMetrics,
      })

      const transformedData = transformResponse(response)
      setData(transformedData)

      // Note: Background calculation removed - backend now uses pre-calculated data
      // which is fast enough to return all metrics in the initial request
    } catch (err: unknown) {
      // Handle different error types
      if (err instanceof Error) {
        // Check for axios error with response
        const axiosError = err as { response?: { data?: { detail?: string; message?: string } } }
        if (axiosError.response?.data) {
          const errorData = axiosError.response.data
          setError(errorData.detail || errorData.message || 'Calculation failed')
        } else {
          setError(err.message || 'An unexpected error occurred')
        }
      } else {
        setError('An unexpected error occurred')
      }
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setLoading(false)
    setLoadingTickers([])
    setError(null)
    setData(null)
  }, [])

  return {
    loading,
    loadingTickers,
    error,
    data,
    calculate,
    calculateSingle,
    calculateBatch,
    removeTickerData,
    reset,
  }
}

export default useCalculation
